// Friendly labels for signed-in Firebase users (Apple/Facebook often omit email).

const looksLikeOpaqueId = (value) => {
    const text = String(value || "").trim();
    if (!text) {
        return true;
    }
    // Apple user ids / raw uids look like long opaque tokens.
    if (/^[0-9a-f.]{20,}$/i.test(text) && !text.includes("@")) {
        return true;
    }
    if (/^user\d{6,}$/i.test(text)) {
        return false;
    }
    return false;
};

const emailLocalPart = (email) => {
    const text = String(email || "").trim();
    if (!text.includes("@")) {
        return "";
    }
    const local = text.split("@")[0];
    if (!local || looksLikeOpaqueId(local)) {
        return "";
    }
    // Hide Apple private-relay gibberish as a primary label.
    if (/privaterelay\.appleid\.com$/i.test(text) && local.length > 12) {
        return "";
    }
    return local;
};

/**
 * Best available human-readable account label for UI.
 * Never returns the string "null" / "undefined".
 */
export function getAuthAccountLabel(user, fallbackUsername = "") {
    if (!user) {
        return "Signed in";
    }

    const username = String(fallbackUsername || "").trim();
    if (username && !looksLikeOpaqueId(username)) {
        return username;
    }

    const displayName = String(user.displayName || "").trim();
    if (displayName && displayName.toLowerCase() !== "null") {
        return displayName;
    }

    const providerName = (user.providerData || [])
        .map((entry) => String(entry?.displayName || "").trim())
        .find(Boolean);
    if (providerName && providerName.toLowerCase() !== "null") {
        return providerName;
    }

    const email =
        String(user.email || "").trim() ||
        (user.providerData || [])
            .map((entry) => String(entry?.email || "").trim())
            .find(Boolean) ||
        "";
    if (email && email.toLowerCase() !== "null") {
        const local = emailLocalPart(email);
        if (local) {
            return local;
        }
        if (!/privaterelay\.appleid\.com$/i.test(email)) {
            return email;
        }
    }

    const providerId = String(
        user.providerData?.[0]?.providerId || ""
    ).toLowerCase();
    if (providerId.includes("apple")) {
        return "Apple account";
    }
    if (providerId.includes("facebook")) {
        return "Facebook account";
    }
    if (providerId.includes("google")) {
        return "Google account";
    }

    return "Signed in";
}

export function buildDisplayNameFromParts(givenName, familyName, email) {
    const given = String(givenName || "").trim();
    const family = String(familyName || "").trim();
    const full = [given, family].filter(Boolean).join(" ").trim();
    if (full) {
        return full.slice(0, 48);
    }
    const local = emailLocalPart(email);
    if (local) {
        return local.slice(0, 48);
    }
    return "";
}
